/**
 * 官网（website layout）顶部导航用的模型列表数据
 *
 * 目标：
 * - 把 `layouts/website.vue` 里超长的常量数组抽离出来，方便维护与复用
 * - 不改变任何业务逻辑（字段名、路径、hot 标记保持一致）
 */

import { filterAllowedModels } from './compliance-models'

export type WebsiteModelNavItem = {
    name: string
    path: string
    hot?: boolean
    model_key: string
    model_name: string
    supportType: string[]
}

const _videoModelList: WebsiteModelNavItem[] = [
    {
        name: 'Veo 3.1',
        path: '/video/veo/veo-3-1',
        hot: true,
        model_key: 'google-veo3-1',
        model_name: 'Veo 3.1',
        supportType: ['text-to-video', 'image-to-video'],
    },
    {
        name: 'Veo 3',
        path: '/video/veo/veo-3',
        model_key: 'google-veo3',
        model_name: 'Veo 3',
        supportType: ['text-to-video', 'image-to-video'],
    },
    {
        name: 'Veo 3 Fast',
        path: '/video/veo/veo-3-fast',
        model_key: 'google-veo3',
        model_name: 'Veo 3 Fast',
        supportType: ['text-to-video', 'image-to-video'],
    },
    {
        name: 'Sora 2',
        path: '/video/sora/sora-2',
        model_key: 'sora',
        model_name: 'Sora 2',
        supportType: ['text-to-video', 'image-to-video'],
    },
    {
        name: 'Kling 2.5',
        path: '/video/kling-ai',
        model_key: 'kling 2.1 standard',
        model_name: 'Kling AI',
        supportType: ['image-to-video'],
    },
    {
        name: 'Kling 2.6 Pro',
        path: '/video/kling-2.6-pro',
        model_key: 'KLING_V2_6_PRO',
        model_name: 'Kling 2.6 Pro',
        supportType: ['text-to-video', 'image-to-video'],
    },
    {
        name: 'Kling O1',
        path: '/video/kling-O1',
        model_key: 'KLING_O1',
        model_name: 'Kling O1',
        supportType: ['image-to-video'],
    },
    {
        name: 'Runway',
        path: '/video/runway-ai',
        model_key: 'RUNWAY',
        model_name: 'Runway AI',
        supportType: ['image-to-video'],
    },
    {
        name: 'Runway Gen-4.5',
        path: '/video/runway-ai-4-5',
        hot: true,
        model_key: 'RUNWAY_4_5',
        model_name: 'Runway Gen-4.5',
        supportType: ['image-to-video'],
    },
    {
        name: 'Hailuo AI',
        path: '/video/hailuo-23',
        model_key: 'hailuo-2-3',
        model_name: 'Hailuo 2.3',
        supportType: ['text-to-video', 'image-to-video'],
    },
    {
        name: 'Seedance',
        path: '/video/seedance',
        model_key: 'Seedance-1.0',
        model_name: 'Seedance',
        supportType: ['text-to-video', 'image-to-video'],
    },
    {
        name: 'Dreamina 3.0',
        path: '/video/dreamina-3',
        model_key: 'DREAMINA_3',
        model_name: 'Dreamina 3.0',
        supportType: ['text-to-video', 'image-to-video'],
    },
    {
        name: 'Wan AI',
        path: '/video/wan-ai',
        model_key: 'WAN_2_5',
        model_name: 'Wan AI',
        supportType: [
            'text-to-image',
            'image-to-image',
            'text-to-video',
            'image-to-video',
        ],
    },
    {
        name: 'Wan 2.6',
        path: '/video/wan-2-6',
        model_key: 'WAN_2_6',
        model_name: 'Wan 2.6',
        supportType: ['text-to-video', 'image-to-video'],
    },
    {
        name: 'Pika 2.2',
        path: '/video/pika-2-2',
        model_key: 'PIKA_2_2',
        model_name: 'Pika 2.2',
        supportType: ['text-to-video', 'image-to-video'],
    },
]

const _imageModelList: WebsiteModelNavItem[] = [
    {
        name: 'Nano Banana Pro',
        path: '/image/nano-banana-pro',
        hot: true,
        model_key: 'CHATBOT_GEMINI_3_PRO_IMAGE_PREVIEW',
        model_name: 'Nano Banana Pro',
        supportType: ['text-to-image', 'image-to-image'],
    },
    {
        name: 'FLUX',
        path: '/image/flux',
        model_key: 'FLUX',
        model_name: 'FLUX',
        supportType: ['text-to-image', 'image-to-image'],
    },
    {
        name: 'Imagen',
        path: '/image/imagen',
        model_key: 'IMAGE4',
        model_name: 'Imagen',
        supportType: ['text-to-image'],
    },
    {
        name: 'GPT-4o',
        path: '/image/gpt-4o',
        model_key: 'GPT_IMAGE_1_MINI',
        model_name: 'GPT-4o',
        supportType: ['text-to-image', 'image-to-image'],
    },
    {
        name: 'GPT Image 1.5',
        path: '/image/gpt-image-1-5',
        model_key: 'GPT_IMAGE_1_5',
        model_name: 'GPT Image 1.5',
        supportType: ['text-to-image', 'image-to-image'],
    },
    {
        name: 'Flux Kontext',
        path: '/image/flux-kontext',
        model_key: 'FLUX',
        model_name: 'FLUX Kontext',
        supportType: ['text-to-image', 'image-to-image'],
    },
    {
        name: 'Qwen Image',
        path: '/image/qwen-image',
        model_key: 'QWEN',
        model_name: 'Qwen Image',
        supportType: ['text-to-image', 'image-to-image'],
    },
    {
        name: 'Wan AI',
        path: '/image/wan-ai',
        model_key: 'WAN_2_5',
        model_name: 'Wan AI',
        supportType: [
            'text-to-image',
            'image-to-image',
            'text-to-video',
            'image-to-video',
        ],
    },
    {
        name: 'Seedream 4.0',
        path: '/image/seedream',
        model_key: 'SEEDREAM',
        model_name: 'SeeDream',
        supportType: ['text-to-image', 'image-to-image'],
    },
    {
        name: 'Seedream 4.5',
        path: '/image/seedream-4-5',
        // hot: true,
        model_key: 'SEEDREAM_4_5',
        model_name: 'Seedream 4.5',
        supportType: ['text-to-image', 'image-to-image'],
    },
    {
        name: 'Dreamina',
        path: '/image/dreamina',
        model_key: 'DREAMINA_4',
        model_name: 'Dreamina',
        supportType: ['text-to-image', 'image-to-image'],
    },
    {
        path: '/image/grok-2',
        name: 'Grok 2 Image',
        model_key: 'GROK_2_IMAGE',
        model_name: 'GROK_2_IMAGE',
        supportType: ['text-to-image'],
    }
]

// 合规：.cn 过滤掉海外模型（详见 utils/compliance-models.ts）
export const videoModelList: WebsiteModelNavItem[] =
    filterAllowedModels(_videoModelList)
export const imageModelList: WebsiteModelNavItem[] =
    filterAllowedModels(_imageModelList)
